/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface BottomNavState {
  /**
   * The box inside the pill where a page's own controls go, once there is one to
   * put them in. Null until the navigation has mounted — and in any app that
   * does not render it at all, which is why whoever fills it has to cope
   * without.
   */
  slot: HTMLElement | null;
  setSlot: (node: HTMLElement | null) => void;
  /** Whether a page is filling the slot, so the navigation stands aside. */
  filled: boolean;
  fill: () => void;
  empty: () => void;
  /** Whether a page has asked for no pill at all — see `useHideBottomNav`. */
  hidden: boolean;
  hide: () => void;
  show: () => void;
}

const BottomNavContext = createContext<BottomNavState>({
  slot: null,
  setSlot: () => {},
  filled: false,
  fill: () => {},
  empty: () => {},
  hidden: false,
  hide: () => {},
  show: () => {},
});

/**
 * The pill at the foot of the screen, and who is using it.
 *
 * There is **one** pill. It usually holds the app's navigation, and a page with
 * controls of its own borrows it rather than covering it with a second one. That
 * is what this carries: a slot to render into, and a count of who is rendering
 * there.
 *
 * Borrowing rather than stacking is the whole point. Two fixed pills in one
 * corner is a thing to keep in step by hand — same radius, same blur, same
 * inset, same safe-area — and they drift the moment either is touched. It also
 * makes the swap animatable, since the two sets of controls are children of one
 * box rather than two elements that know nothing of each other.
 *
 * The same arrangement the breadcrumb has: the chrome belongs to the app, what
 * it should be showing is the page's to say.
 */
export function BottomNavProvider({ children }: { children: React.ReactNode }) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [filledBy, setFilledBy] = useState(0);

  // Counted rather than a flag, so two pages overlapping through a transition
  // cannot have the one unmounting hand the pill back under the one that is
  // still using it.
  const fill = useCallback(() => setFilledBy((count) => count + 1), []);
  const empty = useCallback(() => setFilledBy((count) => Math.max(0, count - 1)), []);

  // Counted for the same reason.
  const [hiddenBy, setHiddenBy] = useState(0);
  const hide = useCallback(() => setHiddenBy((count) => count + 1), []);
  const show = useCallback(() => setHiddenBy((count) => Math.max(0, count - 1)), []);

  const value = useMemo(
    () => ({ slot, setSlot, filled: filledBy > 0, fill, empty, hidden: hiddenBy > 0, hide, show }),
    [slot, filledBy, fill, empty, hiddenBy, hide, show]
  );

  return <BottomNavContext.Provider value={value}>{children}</BottomNavContext.Provider>;
}

export function useBottomNav() {
  return useContext(BottomNavContext);
}

/**
 * Take the pill over for as long as this is mounted, and say where to render.
 *
 * Returns the slot to portal into, or null where there is no navigation to
 * borrow — the gym-admin app renders none, and a component under test usually
 * has none either. A caller that gets null puts its own pill up instead: the
 * controls are the page's, and they cannot depend on the shell being there.
 */
export function useBottomNavSlot(): HTMLElement | null {
  const { slot, fill, empty } = useBottomNav();

  useEffect(() => {
    fill();
    return empty;
  }, [fill, empty]);

  return slot;
}

/**
 * Leave the pill off for as long as this is mounted and `when` holds.
 *
 * For a page that has the whole screen to itself — the climb editor held
 * sideways, where a phone is a few hundred pixels tall and every one of them is
 * the wall's or the form's. Its own controls go elsewhere, not into the slot.
 */
export function useHideBottomNav(when = true) {
  const { hide, show } = useBottomNav();

  useEffect(() => {
    if (!when) return;
    hide();
    return show;
  }, [when, hide, show]);
}
